import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import { usePagamentos } from '../../contexts/PagamentosContext';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import DetalhePrestacao from '../../components/Pagamentos/DetalhePrestacao';
import MetodoPagamentoForm from '../../components/Pagamentos/MetodoPagamentoForm';
import ConfirmacaoPagamento from '../../components/Pagamentos/ConfirmacaoPagamento';
import * as pagamentoService from '../../services/pagamentoService';
import { formatarMoeda } from '../../utils/formatters';

const steps = [
  'Detalhes da Prestação',
  'Método de Pagamento',
  'Confirmação'
];

const RealizarPagamento = ({ prestacao, onSuccess, onStatusChange, onCancel }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [metodoPagamento, setMetodoPagamento] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagamentoCriado, setPagamentoCriado] = useState(null);
  const [statusPagamento, setStatusPagamento] = useState(null);
  const [cancelarVerificacao, setCancelarVerificacao] = useState(null);
  const [detalhesMulta, setDetalhesMulta] = useState(null);
  
  const { realizarPagamento } = usePagamentos();
  const { adicionarNotificacao } = useNotificacao();

  // Calcular multa quando necessário
  useEffect(() => {
    const calcularMulta = async () => {
      if (prestacao.vencida && !detalhesMulta) {
        try {
          const calculo = await pagamentoService.calcularValorComMulta(prestacao.id);
          setDetalhesMulta(calculo);
        } catch (error) {
          console.error('Erro ao calcular multa:', error);
          setError('Erro ao calcular multa. Por favor, tente novamente.');
        }
      }
    };

    calcularMulta();
  }, [prestacao.id, prestacao.vencida, detalhesMulta]);

  // Limpar recursos quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (cancelarVerificacao) {
        cancelarVerificacao();
      }
    };
  }, [cancelarVerificacao]);

  const handleCloseDialog = useCallback(() => {
    if (cancelarVerificacao) {
      cancelarVerificacao();
    }
    setActiveStep(0);
    setMetodoPagamento(null);
    setPagamentoCriado(null);
    setStatusPagamento(null);
    setError(null);
    setCancelarVerificacao(null);
    onCancel();
  }, [cancelarVerificacao, onCancel]);

  const handleNext = useCallback(() => {
    setActiveStep((prevStep) => prevStep + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prevStep) => prevStep - 1);
  }, []);

  const handleMetodoPagamentoChange = useCallback((dados) => {
    setMetodoPagamento(dados);
    setError(null);
  }, []);

  const handleConfirmarPagamento = useCallback(async () => {
    if (!metodoPagamento || !prestacao) {
      setError('Dados de pagamento incompletos');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setStatusPagamento('PROCESSANDO');

      // Usa o valor com multa se disponível
      const valorPagar = detalhesMulta ? detalhesMulta.valorTotal : prestacao.valor;

      const dadosPagamento = {
        prestacaoId: prestacao.id,
        valorPago: valorPagar,
        metodoPagamento: metodoPagamento.metodoPagamento,
        dadosPagamento: {
          ...(metodoPagamento.metodoPagamento === 'MPESA' || metodoPagamento.metodoPagamento === 'EMOLA' 
            ? { 
                numeroTelefone: metodoPagamento.dadosPagamento.numeroTelefone 
              }
            : {
                entidade: metodoPagamento.dadosPagamento.entidade,
                referencia: metodoPagamento.dadosPagamento.referencia
              }
          )
        }
      };

      console.log('Iniciando confirmação de pagamento:', dadosPagamento);
      
      const resultado = await realizarPagamento(dadosPagamento, {
        onSuccess: async (response) => {
          if (!response || !response.id) {
            throw new Error('Resposta inválida do servidor');
          }
          console.log('Pagamento confirmado:', response);
          setStatusPagamento('CONFIRMADO');
          adicionarNotificacao('Pagamento confirmado com sucesso!', 'success');
          setLoading(false);
          handleCloseDialog();
          if (onSuccess) {
            await onSuccess(response);
          }
        },
        onError: (error) => {
          console.error('Erro no pagamento:', error);
          setStatusPagamento('ERRO');
          setError(error.message || 'Erro ao processar pagamento');
          setLoading(false);
        },
        onStatusChange: (novoStatus) => {
          console.log('Novo status:', novoStatus);
          setStatusPagamento(novoStatus);
          if (onStatusChange) {
            onStatusChange(novoStatus);
          }
          
          if (novoStatus === 'CONFIRMADO' || novoStatus === 'REJEITADO') {
            handleCloseDialog();
          }
        },
        onTimeout: () => {
          console.log('Timeout na verificação');
          setStatusPagamento('PENDENTE');
          adicionarNotificacao(
            'O pagamento está pendente. Você será notificado quando for confirmado.',
            'warning'
          );
          handleCloseDialog();
        }
      });

      if (!resultado || !resultado.id) {
        throw new Error('Resultado inválido do pagamento');
      }

      setPagamentoCriado(resultado);
      if (resultado.cancelarVerificacao) {
        setCancelarVerificacao(() => resultado.cancelarVerificacao);
      }
      handleNext();

    } catch (err) {
      console.error('Erro ao realizar pagamento:', err);
      setError(err.message || 'Erro ao realizar pagamento');
      setStatusPagamento('ERRO');
      setLoading(false);
    }
  }, [
    metodoPagamento,
    prestacao,
    detalhesMulta,
    realizarPagamento,
    handleNext,
    onSuccess,
    adicionarNotificacao,
    handleCloseDialog,
    onStatusChange
  ]);

  const renderStepContent = useCallback((step) => {
    switch (step) {
      case 0:
        return (
          <>
            {detalhesMulta && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Pagamento com Multa</AlertTitle>
                <Typography>Valor original: {formatarMoeda(detalhesMulta.valorPrestacao)}</Typography>
                <Typography>Multa: {formatarMoeda(detalhesMulta.valorMulta)}</Typography>
                <Typography>Total a pagar: {formatarMoeda(detalhesMulta.valorTotal)}</Typography>
              </Alert>
            )}
            <DetalhePrestacao 
              prestacao={prestacao} 
            />
          </>
        );
      
      case 1:
        return (
          <MetodoPagamentoForm
            prestacao={prestacao}
            onMetodoPagamentoChange={handleMetodoPagamentoChange}
            metodoPagamento={metodoPagamento?.metodoPagamento}
            error={error}
            valorTotal={detalhesMulta?.valorTotal || prestacao.valor}
          />
        );
      
      case 2:
        return (
          <ConfirmacaoPagamento
            open={true}
            onClose={handleCloseDialog}
            pagamento={{
              ...pagamentoCriado,
              prestacao,
              metodoPagamento: metodoPagamento?.metodoPagamento,
              dadosPagamentoJSON: metodoPagamento?.dadosPagamento,
              status: statusPagamento,
              valorTotal: detalhesMulta?.valorTotal || prestacao.valor
            }}
            loading={loading || statusPagamento === 'PROCESSANDO'}
            error={error}
            success={statusPagamento === 'CONFIRMADO'}
            onConfirmar={handleConfirmarPagamento}
          />
        );
      
      default:
        return null;
    }
  }, [
    prestacao,
    metodoPagamento,
    handleMetodoPagamentoChange,
    error,
    handleCloseDialog,
    pagamentoCriado,
    loading,
    statusPagamento,
    handleConfirmarPagamento,
    detalhesMulta
  ]);

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">
            {error}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0 || loading || statusPagamento === 'PROCESSANDO'}
          onClick={handleBack}
        >
          Voltar
        </Button>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={loading || statusPagamento === 'PROCESSANDO'}
          >
            Cancelar
          </Button>
          
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                loading || 
                (activeStep === 1 && !metodoPagamento)
              }
            >
              Próximo
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleConfirmarPagamento}
              disabled={loading || !metodoPagamento || statusPagamento === 'CONFIRMADO'}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Confirmar Pagamento'
              )}
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

RealizarPagamento.propTypes = {
  prestacao: PropTypes.shape({
    id: PropTypes.number.isRequired,
    mes: PropTypes.string.isRequired,
    valor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    valorComMulta: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.string.isRequired,
    dataVencimento: PropTypes.string.isRequired,
    vencida: PropTypes.bool,
    observacoes: PropTypes.string
  }).isRequired,
  onSuccess: PropTypes.func,
  onStatusChange: PropTypes.func,
  onCancel: PropTypes.func.isRequired
};

export default RealizarPagamento;